import * as stellarApi from './horizon/data/data';
import * as Sentry from "@sentry/react";
import { Asset } from '../../types/types';
import { Claimant, Keypair, Memo, MemoHash, MemoID, MemoReturn, MemoText, Networks, Operation, Asset as StellarAsset, Transaction, TransactionBuilder } from '@stellar/stellar-sdk';

const sponsorAccount = `${import.meta.env.VITE_SPONSOR_CHANNEL_ACCOUNT}`;
const isTestnet = import.meta.env.VITE_isTestnet === 'true' ? true : false;
const networkPhrase = isTestnet ? Networks.TESTNET : Networks.PUBLIC;
const baseReserve = import.meta.env.VITE_STELLAR_BASE_RESERVE ? parseFloat(import.meta.env.VITE_STELLAR_BASE_RESERVE) : 0.5; // default 0.5 XLM


export async function createTrust(assetCode: string, issuer: string, accountKey: string, device_privateKey: string): Promise<[any, Transaction | null]> {

    const keyPair = Keypair.fromSecret(device_privateKey);

    const stellarAsset = new StellarAsset(assetCode, issuer);
    const fee = await stellarApi.getNetworkFee()

    try {

        const sourceAccount = await stellarApi.horizon.loadAccount(accountKey);

        let tx = new TransactionBuilder(sourceAccount, { fee: fee })

            .addOperation(Operation.beginSponsoringFutureReserves({
                source: sponsorAccount, // account paying for the reserves (sponsor account)
                sponsoredId: accountKey,// account for which the trust line will be sponsored
            }))
            .addOperation(Operation.changeTrust({
                asset: stellarAsset,
            }))
            .addOperation(Operation.endSponsoringFutureReserves({
            }))

            .setNetworkPassphrase(networkPhrase)
            .setTimeout(86400) // transaction valid for a day.
            .build();

        tx.sign(keyPair)

        return [null, tx]

    } catch (error) {

        return [error, null]

    }
}

export async function trustAndClaim(assetCode: string, issuer: string, accountKey: string, device_privateKey: string, balanceId: string): Promise<[any, Transaction | null]> {

    const keyPair = Keypair.fromSecret(device_privateKey);
    const stellarAsset = new StellarAsset(assetCode, issuer);
    const fee = await stellarApi.getNetworkFee();

    const sourceAccount = await stellarApi.horizon.loadAccount(accountKey);

    let tx = new TransactionBuilder(sourceAccount, { fee: fee })

        .addOperation(Operation.beginSponsoringFutureReserves({
            source: sponsorAccount, // account paying for the reserves (sponsor account)
            sponsoredId: accountKey,// account for which the trust line will be sponsored
        }))
        .addOperation(Operation.changeTrust({
            asset: stellarAsset,
        }))
        .addOperation(Operation.endSponsoringFutureReserves({
        }))
        .addOperation(Operation.claimClaimableBalance({ balanceId: balanceId }))
        .setNetworkPassphrase(networkPhrase)
        .setTimeout(86400) // transaction valid for a day.
        .build();

    tx.sign(keyPair)

    return [null, tx]



}

export async function claimBalance(balanceId: string, accountKey: string, device_privateKey: string): Promise<[any, Transaction | null]> {

    const keyPair = Keypair.fromSecret(device_privateKey);
    const fee = await stellarApi.getNetworkFee();

    const sourceAccount = await stellarApi.horizon.loadAccount(accountKey);

    let tx = new TransactionBuilder(sourceAccount, { fee: fee })

        .addOperation(Operation.claimClaimableBalance({ balanceId: balanceId }))
        .setNetworkPassphrase(networkPhrase)
        .setTimeout(86400) // transaction valid for a day.
        .build();

    tx.sign(keyPair)

    return [null, tx]

}

export async function builStellarTx(operations: any[], account_key: string, device_privakey: string, memo?: string, memoType?: string): Promise<[any, Transaction | null]> {

    try {

        const keyPair = Keypair.fromSecret(device_privakey);
        const sourceAccount = await stellarApi.horizon.loadAccount(account_key);
        const fee = await stellarApi.getNetworkFee();

        const tx = new TransactionBuilder(sourceAccount, { fee: fee, networkPassphrase: networkPhrase });

        tx.setTimeout(86400) // transaction valid for a day..  

        operations.forEach((op: any) => {
            tx.addOperation(op)
        });

        if (memo && memoType) {

            if (memoType === MemoText) {
                tx.addMemo(Memo.text(memo))
            }

            else if (memoType === MemoID) {
                tx.addMemo(Memo.id(memo))
            }

            else if (memoType === MemoReturn) {
                tx.addMemo(Memo.return(memo))
            }

            else if (memoType === MemoHash) {
                tx.addMemo(Memo.hash(memo))
            }

            else {
                tx.addMemo(Memo.none())
            }

        }

        const builtTx = tx.build()
        builtTx.sign(keyPair)

        return [null, builtTx]

    } catch (error) {

        return [error, null]

    }
}


/** This function does the following:
     *  1. Create the stellar account with initial balance of 1 XLM
     *  2. Adds a trust line for Stellar USDC
     *  3. Sets options to set the master key to 0 and add a device key as alternative signer
     */

export async function createWalletAccount(account_privateKey: string, device_privateKey: string, supportedTrustLineAssets: Asset[]): Promise<[any, Transaction | null]> {

    try {

        const sourceAccount = await stellarApi.horizon.loadAccount(sponsorAccount);
        const fee = await stellarApi.getNetworkFee();

        const assetToTrust = new StellarAsset(supportedTrustLineAssets[0].identifier.split(":")[0], supportedTrustLineAssets[0].identifier.split(":")[1]);

        const accountKeyPair = Keypair.fromSecret(account_privateKey);
        const deviceKeyPair = Keypair.fromSecret(device_privateKey);

        let tx: Transaction

        tx = new TransactionBuilder(sourceAccount, { fee: fee }) // we use the sponsor account here as source of the tx because the account to be sponsored does not exist yet.

            .addOperation(Operation.createAccount({
                destination: accountKeyPair.publicKey(),
                startingBalance: (2 * baseReserve).toString() // this is to deal with the min balance requirement that some onrampers do not understand when you create an account with 0 balance
            }))

            // here we create the trustline for USDC

            .addOperation(Operation.beginSponsoringFutureReserves({
                source: sponsorAccount, // account paying for the reserves (sponsor account)
                sponsoredId: accountKeyPair.publicKey(),// account for which the sponsoring will be done
            }))

            .addOperation(
                Operation.changeTrust({
                    source: accountKeyPair.publicKey(),
                    asset: assetToTrust,
                }),
            )

            // set the weight of the new key to 20, the same with thresholds and set masterKey weight to 0
            .addOperation(Operation.setOptions({
                lowThreshold: 20,
                medThreshold: 20,
                highThreshold: 20,
                masterWeight: 0,
                signer: { ed25519PublicKey: deviceKeyPair.publicKey(), weight: 20 },
                source: accountKeyPair.publicKey()
            }))

            .addOperation(Operation.endSponsoringFutureReserves({
                source: accountKeyPair.publicKey() // The sponsored account must end the sponsorship
            }))

            .setNetworkPassphrase(networkPhrase)
            .setTimeout(86400) // transaction valid for a day.
            .build();

        tx.sign(accountKeyPair) // signature of the sponsoring account happens on the server side

        return [null, tx]

    } catch (error) {

        return [error, null]

    }

}

/**
 * 
 * @param accountToRecover: public key of the account to be recovered 
 * @param recoverySigners: array string with the public keys of the recovery servers 
 * @param device_secretKey: the secret key of the new device key to add as signer
 */
export async function createRemoveDeviceKeySignerTransaction(accountToRecover: string, device_secretKey: string, recoverySigners: string[]): Promise<[any, Transaction | null]> {

    try {

        const sourceAccount = await stellarApi.horizon.loadAccount(accountToRecover);
        const fee = await stellarApi.getNetworkFee();
        const deviceKeyPair = Keypair.fromSecret(device_secretKey);

        const accountAndSignersArray = [accountToRecover, ...recoverySigners];

        // find the previous device key by filtering master key and recovery signers
        let oldDeviceKey = sourceAccount.signers.filter(signer => !accountAndSignersArray.includes(signer.key));

        const tx = new TransactionBuilder(sourceAccount, { fee: fee })

            .addOperation(Operation.setOptions({
                source: accountToRecover,
                signer: { ed25519PublicKey: oldDeviceKey[0].key, weight: 0 },
            }))
            // set the weight of the new key to 20
            .addOperation(Operation.setOptions({
                source: accountToRecover,
                signer: { ed25519PublicKey: deviceKeyPair.publicKey(), weight: 20 },
            }))

            .setNetworkPassphrase(networkPhrase)
            .setTimeout(86400) // transaction valid for a day.
            .build();

        return [null, tx]

    } catch (error) {
        Sentry.captureException(error);
        return [error, null]

    }
}


export async function createPayment(destination: string, account_key: string, device_privateKey: string, amount: string, assetCode?: string, assetIssuer?: string, memo?: string, memoType?: string): Promise<[any, Transaction | null]> {

    try {

        const fee = await stellarApi.getNetworkFee();
        const keyPair = Keypair.fromSecret(device_privateKey);
        let asset = StellarAsset.native();

        if (assetCode && assetIssuer) {

            asset = new StellarAsset(assetCode, assetIssuer);

        }

        const sourceAccount = await stellarApi.horizon.loadAccount(account_key);

        let tx = new TransactionBuilder(sourceAccount, { fee: fee })

            .addOperation(Operation.payment({
                destination: destination,
                asset: asset,
                amount: amount
            }))
            .setNetworkPassphrase(networkPhrase)
            .setTimeout(86400) // transaction valid for a day.
            .addMemo(
                (memo && memoType) ?
                    memoType.toLowerCase() === MemoText ?
                        Memo.text(memo) :
                        memoType.toLowerCase() === MemoID ?
                            Memo.id(memo) :
                            memoType.toLowerCase() === MemoReturn ?
                                Memo.return(memo) :
                                memoType.toLowerCase() === MemoHash ?
                                    Memo.hash(memo) :
                                    Memo.none()
                    :
                    Memo.none()
            )

            .build()

        tx.sign(keyPair);

        return [null, tx]

    } catch (error) {

        return [error, null]

    }
}

/**
     * Method to check for specific if a stellar account trust a specific asset
     * 
     * @param account: the stellar account that trust operation needs to be checked
     * @param assetCode : the stellar asset code (i.e. oEUR)
     * @param assetIssuer: the stellar account representing the issuer of the asset
     * @returns : boolean 
     */

export async function checkAssetTrust(account: string, assetCode: string, assetIssuer: string): Promise<[any, boolean | null]> {

    const [error, response] = await stellarApi.checkAssetTrust(account, assetCode, assetIssuer);

    if (error) {
        Sentry.captureException(error);
        return [error, null]
    }

    return [null, response]
}

/**
     * Method that creates a claimable balance Entry for stellar accounts (the recipient of the payment)
     * that do not have a trustline with the asset is receiving. In this case, the recipient has 
     * a full week to claim the balance. Otherwise, the initiator can reclaim it
     * 
     * @param from: the account creating the claimable balance 
     * @param to: the account for which this payment is intended to 
     */
export async function createClaimableBalance(to: string, accountKey: string, device_privateKey: string, amount: string, assetCode?: string, assetIssuer?: string, memo?: string, memoType?: string) {

    try {

        // check that the destination account exists

        const destinationAccount = await stellarApi.horizon.loadAccount(to);

        let asset = StellarAsset.native()

        if (assetCode && assetIssuer) {

            asset = new StellarAsset(assetCode, assetIssuer);

        }

        if (assetCode && !assetIssuer) {

            const error = new Error('Missing asset Issuer needs to be provided when assetCode is present');

            return [error, null]
        }

        if (!assetCode && assetIssuer) {

            const error = new Error('Missing asset assetCode needs to be provided when assetIssuer is present');

            return [error, null]

        }

        // Create the operation.
        const claimableBalanceEntry = Operation.createClaimableBalance({
            claimants: [
                new Claimant(destinationAccount.account_id, Claimant.predicateUnconditional()),
            ],
            asset: asset,
            amount: amount,
        });

        const [error, stellarTx] = await builStellarTx([claimableBalanceEntry], accountKey, device_privateKey);

        if (error) {
            return [error, null]
        }

        return [null, stellarTx]

    } catch (error) {

        return [error, null]

    }

}

export async function addSigner(accountKey: string, device_privateKey: string, signerPublicKey: string, signerWeight: number): Promise<[any, Transaction | null]> {

    try {

        const fee = await stellarApi.getNetworkFee();
        const keyPair = Keypair.fromSecret(device_privateKey);

        const sourceAccount = await stellarApi.horizon.loadAccount(sponsorAccount);

        let tx = new TransactionBuilder(sourceAccount, { fee: fee })

            .addOperation(Operation.beginSponsoringFutureReserves({
                sponsoredId: accountKey,// account for which the sponsoring will be done
            }))
            .addOperation(Operation.setOptions({
                source: accountKey,
                signer: { ed25519PublicKey: signerPublicKey, weight: signerWeight },
            }))
            .addOperation(Operation.endSponsoringFutureReserves({
                source: accountKey
            }))
            .setNetworkPassphrase(networkPhrase)
            .setTimeout(86400) // transaction valid for a day.
            .build()

        tx.sign(keyPair);

        return [null, tx]

    } catch (error) {

        return [error, null]

    }

}

export async function createMigrateAccountTransaction(accountKey: string, oldDPK: string, newDPK: string, signerPublicKey: string, signerWeight: number): Promise<[any, Transaction | null]> {

    try {
        const sourceAccount = await stellarApi.horizon.loadAccount(accountKey);
        const fee = await stellarApi.getNetworkFee();
        const oldDeviceKey = Keypair.fromSecret(oldDPK);
        const newDeviceKey = Keypair.fromSecret(newDPK);
        let addMinimumBalanceBuffer = false;

        sourceAccount.balances.forEach(balance => {

            if (balance.asset_type === 'native' && parseFloat(balance.balance) < 1.5) {
                addMinimumBalanceBuffer = true;
            }
        }
        );

        let tx = new TransactionBuilder(sourceAccount, { fee: fee });

        if (addMinimumBalanceBuffer) {

            // add a buffer to ensure the account has enough balance to cover min balance after removing signers and trustlines
            tx.addOperation(Operation.payment({
                source: sponsorAccount,
                destination: accountKey,
                asset: StellarAsset.native(),
                amount: (2 * baseReserve).toString() // adding 1 XLM buffer
            }));
        }

        // remove the signers 

        sourceAccount.signers.forEach(signer => {

            if (signer.key !== accountKey && signer.key !== signerPublicKey && signer.key !== newDeviceKey.publicKey()) {

                tx.addOperation(Operation.setOptions({
                    source: accountKey,
                    signer: { ed25519PublicKey: signer.key, weight: 0 },
                }))

            }
        });

        // set master key weight to 0 and thresholds to 20
        tx.addOperation(Operation.setOptions({
            source: accountKey,
            masterWeight: 0,
            lowThreshold: 20,
            medThreshold: 20,
            highThreshold: 20
        }));

        // set the weight of the new device key to 20

        tx.addOperation(Operation.setOptions({
            source: accountKey,
            signer: { ed25519PublicKey: newDeviceKey.publicKey(), weight: signerWeight },
        }));

        // set the weight of the new recovery key to 20
        tx.addOperation(Operation.setOptions({
            source: accountKey,
            signer: { ed25519PublicKey: signerPublicKey, weight: signerWeight },
        }));

        tx.setNetworkPassphrase(networkPhrase)
        tx.setTimeout(86400) // transaction valid for a day.

        const builtTx = tx.build()
        builtTx.sign(oldDeviceKey);


        return [null, builtTx]

    } catch (error) {

        return [error, null]
    }







}




